import React, { useEffect, useState } from 'react';
import { commandCenterApi } from '../services/api';
import { useCanSeeRevenue } from '../lib/perms';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { ScrollArea } from './ui/scroll-area';
import { IndianRupee, X, Loader2, CheckCircle2, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';

const DISMISS_KEY = 'dashboard_missing_ctc_dismissed_at';
const DISMISS_TTL_MS = 12 * 60 * 60 * 1000; // 12 hours — re-surface twice a day

/**
 * Dashboard banner that surfaces "Joined" applications with no CTC on file.
 * Shows a one-line warning + opens a backfill dialog where the user can
 * type the offered CTC for each application inline. Auto-hides when count = 0
 * and dismissible for 12h per browser.
 */
const MissingCtcBanner = () => {
  const canSeeRevenue = useCanSeeRevenue();
  const [count, setCount] = useState(0);
  const [items, setItems] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [open, setOpen] = useState(false);
  const [drafts, setDrafts] = useState({}); // appId -> string
  const [savingId, setSavingId] = useState(null);
  const [dismissed, setDismissed] = useState(() => {
    try {
      const at = parseInt(localStorage.getItem(DISMISS_KEY) || '0', 10);
      return at && Date.now() - at < DISMISS_TTL_MS;
    } catch {
      return false;
    }
  });

  const fetchData = () => {
    commandCenterApi
      .getMissingCtc(50)
      .then((r) => {
        setCount(r.data?.count || 0);
        setItems(r.data?.items || []);
      })
      .catch(() => {
        // Endpoint may be missing on older backend — fail quiet
      })
      .finally(() => setLoaded(true));
  };

  useEffect(() => {
    fetchData();
  }, []);

  const dismiss = () => {
    try {
      localStorage.setItem(DISMISS_KEY, String(Date.now()));
    } catch {
      // ignore
    }
    setDismissed(true);
  };

  const saveOne = async (appId) => {
    const raw = String(drafts[appId] ?? '').trim();
    if (raw === '') {
      toast.error('Enter an Offered CTC value (in Lakhs).');
      return;
    }
    const n = Number(raw);
    if (Number.isNaN(n) || n <= 0) {
      toast.error('Offered CTC must be a positive number.');
      return;
    }
    setSavingId(appId);
    try {
      // User types in LPA (e.g. 12.5). Convert to rupees so all aggregations
      // share a single unit (matches the Pipeline / Command Center stage-change
      // dialogs and the closure-of-week / candidate-CTC totals).
      await commandCenterApi.setApplicationOfferedCtc(appId, Math.round(n * 100000));
      toast.success('Offered CTC saved · totals updated');
      // Remove this row from the list optimistically
      setItems((prev) => prev.filter((it) => it.application_id !== appId));
      setCount((c) => Math.max(0, c - 1));
      setDrafts((prev) => {
        const next = { ...prev };
        delete next[appId];
        return next;
      });
      // Notify any listening dashboards (My Team / Command Center) so they
      // can refetch their CTC tiles without a full page reload.
      try {
        window.dispatchEvent(new CustomEvent('ctc:backfilled', { detail: { application_id: appId } }));
      } catch {
        // ignore (older browsers)
      }
    } catch (e) {
      toast.error(
        e?.response?.data?.detail || 'Failed to save offered CTC. Please try again.'
      );
    } finally {
      setSavingId(null);
    }
  };

  if (!canSeeRevenue || !loaded || count === 0 || dismissed) {
    return null;
  }

  return (
    <>
      <div
        data-testid="missing-ctc-banner"
        className="rounded-lg border border-amber-200 bg-gradient-to-r from-amber-50 to-orange-50 p-3 flex items-center gap-3 shadow-sm"
      >
        <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold text-amber-900">
            {count === 1
              ? '1 closure has no CTC on file'
              : `${count} closures have no CTC on file`}
          </div>
          <div className="text-xs text-amber-800/80 mt-0.5">
            Your <span className="font-semibold">Joined CTC</span> total may be understated. Add the offered CTC to fix.
          </div>
        </div>
        <Button
          size="sm"
          onClick={() => setOpen(true)}
          data-testid="missing-ctc-fix-btn"
          className="h-8 bg-amber-600 hover:bg-amber-700 text-white"
        >
          <IndianRupee className="w-3.5 h-3.5 mr-1" />
          Fix now
        </Button>
        <button
          type="button"
          onClick={dismiss}
          title="Dismiss for 12 hours"
          data-testid="missing-ctc-dismiss"
          className="w-7 h-7 rounded-md hover:bg-amber-100 flex items-center justify-center text-amber-700"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl" data-testid="missing-ctc-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <IndianRupee className="w-5 h-5 text-amber-600" />
              Backfill Offered CTC
            </DialogTitle>
            <DialogDescription>
              Enter the Offered CTC (in Lakhs) for each closure below. Saved values immediately update your Joined CTC total.
            </DialogDescription>
          </DialogHeader>

          {items.length === 0 ? (
            <div className="text-center py-8 text-emerald-700">
              <CheckCircle2 className="w-8 h-8 mx-auto mb-2" />
              <div className="text-sm font-semibold">All closures have CTC on file</div>
            </div>
          ) : (
            <ScrollArea className="max-h-[60vh] pr-2">
              <ul className="divide-y divide-slate-100">
                {items.map((it) => {
                  const draft = drafts[it.application_id] ?? '';
                  const saving = savingId === it.application_id;
                  return (
                    <li
                      key={it.application_id}
                      className="py-3 flex items-center gap-3"
                      data-testid={`missing-ctc-row-${it.application_id}`}
                    >
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-semibold text-slate-900 truncate">
                          {it.candidate_name}
                        </div>
                        <div className="text-xs text-slate-500 truncate">
                          {it.job_title}
                          {it.client_name ? ` · ${it.client_name}` : ''}
                          {it.joined_at
                            ? ` · joined ${format(new Date(it.joined_at), 'dd MMM yyyy')}`
                            : ''}
                        </div>
                      </div>
                      <div className="flex items-center gap-1 flex-shrink-0">
                        <span className="text-xs text-slate-500">₹</span>
                        <Input
                          type="number"
                          step="0.01"
                          min="0"
                          value={draft}
                          onChange={(e) =>
                            setDrafts((prev) => ({
                              ...prev,
                              [it.application_id]: e.target.value,
                            }))
                          }
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              saveOne(it.application_id);
                            }
                          }}
                          placeholder="e.g. 12.5"
                          className="h-8 w-24 text-sm"
                          disabled={saving}
                          data-testid={`missing-ctc-input-${it.application_id}`}
                        />
                        <span className="text-[10px] text-slate-500 mr-1">L</span>
                        <Button
                          size="sm"
                          onClick={() => saveOne(it.application_id)}
                          disabled={saving || !draft}
                          data-testid={`missing-ctc-save-${it.application_id}`}
                          className="h-8 px-3"
                        >
                          {saving ? (
                            <Loader2 className="w-3.5 h-3.5 animate-spin" />
                          ) : (
                            'Save'
                          )}
                        </Button>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </ScrollArea>
          )}

          <div className="flex justify-between items-center pt-2 border-t border-slate-100">
            <span className="text-xs text-slate-500">
              {items.length} closure{items.length === 1 ? '' : 's'} pending
            </span>
            <Button variant="outline" onClick={() => setOpen(false)} data-testid="missing-ctc-close">
              Done
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default MissingCtcBanner;
